/**
 * Same as UserInviteOptions.tsx just without
 * the option to remove the existing users via the input box
 */
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {maybePluralize} from '@wandb/weave/core/util/string';
import {Maybe} from 'graphql/jsutils/Maybe';
import React, {FC, useContext} from 'react';
import {components, MultiValue, OptionProps} from 'react-select';
import {FilterOptionOption} from 'react-select/dist/declarations/src/filters'; // eslint-disable-line import/no-unresolved
// eslint-disable-next-line wandb/no-deprecated-imports
import {Image} from 'semantic-ui-react';

import {UserAccountType} from '../../../../generated/graphql';
import {useViewer} from '../../../../state/viewer/hooks';
import {checkIsServiceAccount} from '../../../../util/serviceAccounts';
import {OrganizationMemberRole} from '../../../MembersTable/util';
import {useRegistryAccountSettings} from '../../common/RegistryAccountSettings';
import {RegistryMembersContext} from '../RegistryMembersContextProvider';
import {
  QueryOrganizationTeam,
  QueryRegistryOrganizationMember,
  QueryRegistryOrganizationServiceAccount,
  RegistryMemberType,
} from './types';
import {getRegistryUserEmail} from './utils';

export type Member = {
  type: RegistryMemberType;
  id?: Maybe<string>;
  username?: Maybe<string>;
  photoUrl?: Maybe<string>;
  name: string;
  email?: Maybe<string>;
  accountType?: Maybe<string>;
  numUsers: Maybe<number>;
};

export interface MemberInviteOption {
  value: string;
  label: string;
  type: RegistryMemberType;
  username: Maybe<string>;
  photoUrl: Maybe<string> | undefined;
  name: Maybe<string>;
  email: Maybe<string>;
  accountType: Maybe<UserAccountType>;
  numUsers: Maybe<number>;
}

interface MemberInviteOptionsProps {
  selectedUsers: string[];
  setSelectedUsers: (users: string[]) => void;
  selectedTeams: string[];
  setSelectedTeams: (teams: string[]) => void;
}

export const MemberInviteOptions: FC<MemberInviteOptionsProps> = ({
  selectedUsers,
  setSelectedUsers,
  selectedTeams,
  setSelectedTeams,
}) => {
  const viewer = useViewer();

  const {
    registryMembers,
    registryTeamMembers,
    organizationMembersFiltered,
    organizationTeams,
    serviceAccounts,
  } = useContext(RegistryMembersContext);
  const registryMembersIds = registryMembers.map(member => member.id);
  const registryTeamMembersIds = registryTeamMembers.map(member => member.id);

  let orgMembers = getOrgMembers(
    organizationMembersFiltered,
    organizationTeams,
    serviceAccounts
  );

  // This allows org admins to add themselves to the registry
  const {viewerMemberRole} = useRegistryAccountSettings();
  const isViewerAdmin =
    viewer?.admin || viewerMemberRole === OrganizationMemberRole.ADMIN;
  if (!isViewerAdmin) {
    orgMembers = orgMembers.filter(
      u => u.id && u.id !== viewer?.id && !registryMembersIds.includes(u.id)
    );
  }

  const options: MemberInviteOption[] = orgMembers
    .filter(u => u.id && !registryMembersIds.includes(u.id))
    .filter(u => u.id && !registryTeamMembersIds.includes(u.id))
    .map(u => {
      return {
        value: u.id as string,
        label: u.name,
        type: u.type,
        username: u.username,
        photoUrl: u.photoUrl,
        name: u.name,
        email: u.email,
        accountType: u.accountType as UserAccountType,
        numUsers: u.numUsers,
      };
    });

  const currentValue = options.filter(
    u => selectedUsers.includes(u.value) || selectedTeams.includes(u.value)
  );

  const customFilter = (
    option: FilterOptionOption<MemberInviteOption>,
    inputValue: string
  ): boolean => {
    const input = inputValue.toLowerCase();
    const matchesName = option.data.name?.toLowerCase().includes(input);
    if (matchesName) {
      return true;
    }
    const matchesUsername = option.data.username?.toLowerCase().includes(input);
    if (matchesUsername) {
      return true;
    }
    const matchesEmail = option.data.email?.toLowerCase().includes(input);
    if (matchesEmail) {
      return true;
    }
    return false;
  };
  return (
    <Select<MemberInviteOption, true>
      className={'w-full'}
      size={'variable'}
      value={currentValue}
      options={options}
      placeholder="Search name, email, username or team name"
      isMulti
      isSearchable
      filterOption={customFilter}
      closeMenuOnSelect={false}
      onChange={(opt: MultiValue<MemberInviteOption>) => {
        setSelectedUsers(
          opt
            .filter(o => o.type === RegistryMemberType.User)
            .map(o => o.value) ?? []
        );
        setSelectedTeams(
          opt
            .filter(o => o.type === RegistryMemberType.Team)
            .map(o => o.value) ?? []
        );
      }}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        Option: MultiMemberInviteOptionComp,
      }}
    />
  );
};

const MultiMemberInviteOptionComp: React.FC<
  OptionProps<MemberInviteOption, true>
> = props => {
  const {data} = props;
  const {photoUrl, username, email, name, accountType, type, numUsers} = data;

  return (
    <components.Option {...props}>
      <MemberInviteOptionComp
        photoUrl={photoUrl}
        name={name}
        username={username}
        email={email}
        accountType={accountType}
        type={type}
        numUsers={numUsers}
      />
    </components.Option>
  );
};

export const MemberInviteOptionComp: React.FC<
  Pick<
    MemberInviteOption,
    | 'type'
    | 'photoUrl'
    | 'username'
    | 'email'
    | 'name'
    | 'accountType'
    | 'numUsers'
  >
> = props => {
  const {photoUrl, username, email, name, accountType, type, numUsers} = props;

  return (
    <Tailwind>
      <div className={'flex items-center'}>
        {type === RegistryMemberType.Team && (
          <div className="flex items-center gap-8">
            {photoUrl != null ? (
              <img src={photoUrl} className="h-32 w-32 rounded-full" alt="" />
            ) : (
              <div className="content-center justify-center rounded-full bg-teal-300 bg-opacity-[.48] p-6 align-middle">
                <Icon name="users-team" className="h-20 w-20 text-teal-600" />
              </div>
            )}
            <div>
              <p className="text-base leading-[22px]">{name}</p>
              <div className="text-base leading-5 text-moon-500">
                <span>{type}</span>
                <span className="font-semibold"> · </span>
                <span>{maybePluralize(numUsers ?? 0, 'user')}</span>
              </div>
            </div>
          </div>
        )}
        {type === RegistryMemberType.User && (
          <>
            {!checkIsServiceAccount(accountType) ? (
              <Image
                avatar
                src={
                  photoUrl === '' ? '/default-profile-picture.png' : photoUrl
                }
              />
            ) : (
              <div className="mr-6 content-center justify-center rounded-full bg-teal-300 bg-opacity-[.48] p-6 align-middle">
                <Icon
                  name="robot-service-member"
                  className="h-20 w-20 text-teal-600"
                />
              </div>
            )}
            <div className={'flex flex-col items-center'}>
              <div className={'self-start'}>
                <span>{name}</span>
                {username && (
                  <span className={'ml-6 text-sm text-moon-500'}>
                    @{username}
                  </span>
                )}
              </div>
              <span className={'-mt-2 self-start text-sm text-moon-500'}>
                {email}
              </span>
            </div>
          </>
        )}
      </div>
    </Tailwind>
  );
};

const getOrgMembers = (
  orgMembers: QueryRegistryOrganizationMember[],
  organizationTeams: Omit<QueryOrganizationTeam, 'members'>[],
  serviceAccounts: QueryRegistryOrganizationServiceAccount[]
) => {
  const members: Member[] = orgMembers.map(orgMember => ({
    type: RegistryMemberType.User,
    username: orgMember.username,
    name: orgMember.name,
    email: orgMember.email,
    id: orgMember.user.id,
    accountType: orgMember.user.accountType,
    photoUrl: orgMember.photoUrl,
    numUsers: null,
  }));

  serviceAccounts.forEach(serviceAccount => {
    members.push({
      type: RegistryMemberType.User,
      username: '',
      name: serviceAccount.name,
      email: getRegistryUserEmail(
        serviceAccount.accountType ?? UserAccountType.OrgService,
        undefined
      ),
      id: serviceAccount.id,
      accountType: serviceAccount.accountType,
      photoUrl: '',
      numUsers: null,
    });
  });

  organizationTeams.forEach(team => {
    members.push({
      type: RegistryMemberType.Team,
      username: '',
      name: team.name,
      email: '',
      id: team.id,
      accountType: null,
      photoUrl: team.photoUrl,
      numUsers: team.memberCount,
    });
  });

  return members;
};
